import { Button } from '~/components/ui/button'
import { ErrorBanner } from '~/components/ui/error-banner'
import { ChatThinking } from '~/components/chat/chat-thinking'
import { Typewriter } from '~/components/chat/typewriter'
import { CustomSelect } from '~/components/ui/custom-select'
import { cn } from '~/lib/utils'
import {
    useSurveyPanel,
    type UseSurveyPanelParams,
} from '~/hooks/use-survey-panel'

export interface SurveyPanelProps extends UseSurveyPanelParams {
    /** done 상태에서 채점하기를 눌렀을 때. */
    onScore(): void
}

/**
 * 좌측 대화형 설문 패널(턴 기반, 스트리밍 아님) — 렌더 전용.
 * 상태·턴 로직은 `useSurveyPanel` 훅이 보유한다.
 */
export function SurveyPanel(props: SurveyPanelProps) {
    const { onScore, scoring } = props
    const {
        bubbles,
        turn,
        draft,
        setDraft,
        selectedIndustry,
        setSelectedIndustry,
        sending,
        error,
        scrollRef,
        textareaRef,
        done,
        phase,
        locked,
        inSurvey,
        lastAssistantIndex,
        showIndustryHelpers,
        answerChoices,
        sendMessage,
        confirmIndustry,
        navTo,
    } = useSurveyPanel(props)

    return (
        <div className='border-border bg-card flex min-h-0 min-w-0 flex-1 flex-col rounded-md border'>
            {/* 대화 로그 */}
            <div
                ref={scrollRef}
                className='min-h-0 flex-1 space-y-3 overflow-y-auto p-3'
                role='log'
                aria-live='polite'
                aria-label='설문 대화'>
                {bubbles.length === 0 && !error ? (
                    <p className='text-muted-foreground text-sm'>
                        설문을 준비하고 있어요…
                    </p>
                ) : null}

                {bubbles.map((b, index) => {
                    const isUser = b.role === 'user'
                    // 새로 도착한 마지막 AI 질문만 타이핑 효과를 준다(과거 메시지 재타이핑 방지).
                    const typing =
                        !isUser && !b.optimistic && index === lastAssistantIndex
                    return (
                        <div
                            key={b.id}
                            className={cn(
                                'flex w-full',
                                isUser ? 'justify-end' : 'justify-start'
                            )}>
                            <div
                                className={cn(
                                    'max-w-[85%] rounded-md px-3.5 py-2.5 text-sm leading-relaxed break-words whitespace-pre-wrap',
                                    isUser
                                        ? 'bg-primary text-primary-foreground'
                                        : 'border-border bg-muted text-foreground border'
                                )}>
                                <span className='sr-only'>
                                    {isUser ? '나: ' : 'AI: '}
                                </span>
                                {typing ? (
                                    <Typewriter
                                        text={b.content}
                                        markdown={false}
                                    />
                                ) : (
                                    b.content
                                )}
                            </div>
                        </div>
                    )
                })}

                {sending ? (
                    <div className='flex justify-start'>
                        <ChatThinking
                            phrases={[
                                '답변을 확인하는 중',
                                '다음 질문을 준비하는 중',
                            ]}
                        />
                    </div>
                ) : null}
            </div>

            {/* 입력/액션 영역 */}
            <div className='border-border border-t p-3'>
                {error ? (
                    <ErrorBanner className='mb-2'>{error}</ErrorBanner>
                ) : null}

                <div className='space-y-3'>
                    {done ? (
                        <ErrorBanner tone='success'>
                            모든 문항에 답했어요. 답을 수정하거나 아래에서
                            채점할 수 있어요.
                        </ErrorBanner>
                    ) : null}

                    {/* 선택형 HITL — 챗폼 바로 위 이동/채점 행보다 더 위에 둔다. */}
                    {answerChoices.length > 0 ? (
                        <div className='flex flex-wrap gap-1.5'>
                            {answerChoices.map((ch) => (
                                <button
                                    key={ch}
                                    type='button'
                                    onClick={() => sendMessage(ch)}
                                    disabled={locked}
                                    className='border-primary/40 text-primary hover:bg-accent bg-card rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors disabled:opacity-50'>
                                    {ch}
                                </button>
                            ))}
                        </div>
                    ) : null}

                    {inSurvey ? (
                        <div className='flex items-center justify-between gap-2'>
                            <div className='flex gap-2'>
                                <Button
                                    variant='secondary'
                                    size='sm'
                                    onClick={() => navTo('prev')}
                                    disabled={locked}>
                                    ← 이전
                                </Button>
                                <Button
                                    variant='secondary'
                                    size='sm'
                                    onClick={() => navTo('next')}
                                    disabled={locked}>
                                    다음 →
                                </Button>
                            </div>
                            <Button
                                size='sm'
                                onClick={onScore}
                                disabled={scoring}>
                                {scoring ? '채점 중…' : '채점하기'}
                            </Button>
                        </div>
                    ) : null}

                    {showIndustryHelpers ? (
                        <div className='border-border bg-muted space-y-2 rounded-md border p-3'>
                            {turn?.proposedIndustry ? (
                                <div className='flex flex-wrap items-center gap-2'>
                                    <span className='text-foreground text-sm'>
                                        <span className='font-medium'>
                                            {turn.proposedIndustry}
                                        </span>{' '}
                                        맞나요?
                                    </span>
                                    <Button
                                        size='sm'
                                        onClick={() =>
                                            confirmIndustry(
                                                turn.proposedIndustry!
                                            )
                                        }
                                        disabled={locked}>
                                        확인
                                    </Button>
                                </div>
                            ) : null}

                            {turn && turn.industryOptions.length > 0 ? (
                                <div className='space-y-1.5'>
                                    <label
                                        className='text-muted-foreground text-xs font-medium'
                                        htmlFor='industry-fallback'>
                                        목록에서 업종 선택(직접 확정)
                                    </label>
                                    <div className='flex gap-2'>
                                        <CustomSelect
                                            id='industry-fallback'
                                            value={selectedIndustry}
                                            onValueChange={setSelectedIndustry}
                                            placeholder='선택하세요'
                                            ariaLabel='업종 선택'
                                            disabled={locked}
                                            containerClassName='min-w-0 flex-1'
                                            className='border-border h-9'
                                            options={turn.industryOptions.map(
                                                (opt) => ({
                                                    value: opt,
                                                    label: opt,
                                                })
                                            )}
                                        />
                                        <Button
                                            variant='secondary'
                                            size='sm'
                                            onClick={() =>
                                                confirmIndustry(
                                                    selectedIndustry
                                                )
                                            }
                                            disabled={
                                                locked || !selectedIndustry
                                            }>
                                            확정
                                        </Button>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {/* 자유서술 답변 입력 */}
                    <form
                        onSubmit={(e) => {
                            e.preventDefault()
                            sendMessage(draft)
                        }}
                        className='flex items-end gap-2'>
                        <label className='sr-only' htmlFor='survey-answer'>
                            {phase === 'industry' ? '업종 답변' : '문항 답변'}
                        </label>
                        <textarea
                            ref={textareaRef}
                            id='survey-answer'
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault()
                                    sendMessage(draft)
                                }
                            }}
                            disabled={locked}
                            rows={1}
                            placeholder={
                                phase === 'industry'
                                    ? '예: 도시락·김밥을 만드는 즉석조리식품 공장이에요'
                                    : '답변을 입력하세요 (Enter 전송, Shift+Enter 줄바꿈)'
                            }
                            className='border-border bg-card text-foreground placeholder:text-muted-foreground focus-visible:ring-ring max-h-40 min-h-[2.5rem] flex-1 resize-none rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'
                        />
                        <Button
                            type='submit'
                            disabled={locked || !draft.trim()}>
                            전송
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    )
}
